<script>
export default {
  name: 'SidebarLink',
  props: {
    badgeText: [Number, String],
    badgeType: String,
    disabled: Boolean,
    href: {
      type: String,
      default: '#'
    },
    icon: String,
    loading: Boolean,
    text: String,
    to: [String, Object]
  },
  computed: {
    isCurrent() {
      if (this.to) {
        // If the to.params object has undefined keys, we don't want to run the $router.resolve()
        // method because it will throw warnings. This usually happens when a link is rendered
        // before some async data has loaded.
        const params = this.to.params;
        const paramValues = params ? [...Object.keys(params)].map(key => params[key]) : [];
        const hasUndefinedParams = !!paramValues.filter(val => typeof val === 'undefined').length;

        if (hasUndefinedParams) {
          return false;
        } else {
          const link = this.$router.resolve(this.to);
          return link && link.location.path === location.pathname;
        }
      } else {
        return false;
      }
    },

    external() {
      return !!this.href.match(/^https?:\/\//);
    }
  },
  methods: {
    handleClick(event) {

      // Disabled links can't be clicked
      if (this.disabled) {
        event.preventDefault();
        return;
      }

      // Intercept router links
      if (this.to) {
        event.preventDefault();
        this.$router.push(this.to);
        return;
      }

      // Ignore empty links
      if (this.href === '#') {
        event.preventDefault();
      }

      // Emit the click event
      this.$emit('click');
    }
  }
};
</script>

<template>
  <a
    :class="{
      'sidebar-link': true,
      'is-current': isCurrent,
      'is-disabled': disabled,
    }"
    :href="href || '#'"
    :target="external ? '_blank' : null"
    :rel="external ? 'noopener' : null"
    @click="handleClick"
  >
    <i v-if="loading" class="el-icon-fa-circle-notch el-icon-fa-spin" />
    <i v-else :class="`${icon}`" />

    {{ text }}

    <transition name="fade">
      <sup
        v-if="badgeText"
        :class="{
          'count': true,
          'is-warning': badgeType === 'warning',
          'is-success': badgeType === 'success',
          'is-primary': badgeType === 'primary'
        }"
      >
        {{ badgeText }}
      </sup>
    </transition>
  </a>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.sidebar-link {
  display: block;
  position: relative;
  color: $--color-gray-20;
  padding: .5rem 1.25rem .5rem 2.75rem;
  transition: .1s color;

  i {
    position: absolute;
    top: .75em;
    left: 1.25rem;
    width: 1rem;
    text-align: center;
    color: $--color-gray-60;
    margin-right: .5rem;
    transition: .1s color;
  }

  .count {
    position: absolute;
    top: 0;
    display: inline-block;
    background-color: $--color-danger;
    font-size: .75em;
    color: $--color-white;
    border-radius: 1em;
    padding: .125em .5em;

    &.is-primary {
      background-color: $--color-primary;
    }

    &.is-success {
      background-color: $--color-success;
    }

    &.is-warning {
      background-color: $--color-warning;
    }

    &.fade-enter-active,
    &.fade-leave-active {
      transition: opacity .25s, transform .25s;
    }

    &.fade-enter,
    &.fade-leave-to {
      opacity: 0;
      transform: scale(.25);
    }
  }

  &:hover {
    color: $--color-white;

    i {
      color: lighten($--color-gray-60, 5%);
    }
  }

  &.is-current {
    background-color: $--color-primary;
    color: $--color-white;

    i {
      color: $--color-white;
    }
  }

  &.is-disabled {
    color: $--color-gray-60;
    cursor: not-allowed;

    i {
      color: $--color-gray-70;
    }
  }
}
</style>
